import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Alert,
  FormControl,
  FormHelperText,
  RadioGroup,
  Radio,
  FormLabel,
  FormControlLabel,
  Divider,
  CircularProgress,
} from "@mui/material";
import { Trans, useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import { Guest } from "../types";
import { api } from "../services/api";
import { GuestTypeBadge } from "./GuestTypeBadge";

export const RsvpForm: React.FC = () => {
  const { t } = useTranslation();
  const { currentUser, guestData, loading } = useAuth();
  const [guests, setGuests] = useState<Guest[]>([]);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [formErrors, setFormErrors] = useState<{ [key: string]: boolean }>({});

  const contentSx = {
    textAlign: "center",
    maxWidth: "800px",
    mx: "auto",
    fontSize: { xs: "1rem", md: "1.1rem" },
    lineHeight: 1.8,
    fontFamily: "'Raleway', sans-serif",
    px: { xs: 2, md: 0 },
    pb: 2,
  };

  // Initialize form with guest data
  useEffect(() => {
    if (guestData?.guests) {
      setGuests(guestData.guests);
    }
  }, [guestData]);

  const validateForm = () => {
    const errors: { [key: string]: boolean } = {};
    guests.forEach((guest, index) => {
      if (guest.isAttending === null) {
        errors[`attendance-${index}`] = true;
      }
    });
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      setError(t("rsvpForm.mandatoryFields"));
      return;
    }
    try {
      await api.submitRSVP(guests);
      setSubmitted(true);
      setError("");
    } catch (err) {
      setError(t("rsvpForm.error"));
    }
  };

  const updateGuest = (
    index: number,
    field: keyof Guest,
    value: string | number | null
  ) => {
    const newGuests = [...guests];
    newGuests[index] = {
      ...newGuests[index],
      [field]: value,
    };
    setGuests(newGuests);
    if (field === "isAttending") {
      setFormErrors({ ...formErrors, [`attendance-${index}`]: false });
    }
  };

  if (!currentUser) {
    return (
      <Alert severity="info">
        <Trans i18nKey="rsvpForm.loginPrompt">
          Vänligen <a href="/login">logga in</a> med dina inbjudningsuppgifter
          för att skicka ditt OSA senast 1:a juli.
        </Trans>
      </Alert>
    );
  }

  if (loading) {
    return (
      <Paper sx={{ p: 3, maxWidth: 600, mx: "auto" }}>
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <CircularProgress />
        </Box>
      </Paper>
    );
  }

  if (!guestData?.guests?.length) {
    return (
      <Paper sx={{ p: 3, maxWidth: 600, mx: "auto" }}>
        <Alert severity="info">{t("rsvpForm.noInvitation")}</Alert>
      </Paper>
    );
  }

  if (submitted) {
    return (
      <Paper sx={{ p: 3, maxWidth: 600, mx: "auto" }}>
        <Alert severity="success">{t("rsvpForm.thankYou")}</Alert>
      </Paper>
    );
  }

  return (
    <>
      <Typography sx={contentSx}>{t("rsvpForm.rsvpDeadline")}</Typography>
      <Paper
        sx={{
          p: { xs: 2, md: 3 },
          maxWidth: 600,
          mx: "auto",
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <form onSubmit={handleSubmit}>
          {guests.map((guest, index) => (
            <React.Fragment key={guest.id}>
              {index > 0 && (
                <Divider
                  sx={{
                    my: 4,
                    borderColor: "rgba(0, 0, 0, 0.12)",
                    borderWidth: 0.75,
                  }}
                />
              )}
              <Box sx={{ mb: 4 }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontFamily: "'Raleway', sans-serif",
                      fontSize: { xs: "1.1rem", md: "1.25rem" },
                    }}
                  >
                    {guest.name}
                  </Typography>
                  <GuestTypeBadge guestType={guest.guestType} sx={{ ml: 1 }} />
                </Box>
                <FormControl
                  required
                  error={formErrors[`attendance-${index}`]}
                  component="fieldset"
                  sx={{ mt: 0.5, mb: 1, width: "100%" }}
                >
                  <FormLabel
                    component="legend"
                    sx={{
                      fontFamily: "'Raleway', sans-serif",
                      fontSize: { xs: "0.9rem", md: "1rem" },
                    }}
                  >
                    {t("rsvpForm.willAttend")}
                  </FormLabel>
                  <RadioGroup
                    row
                    value={
                      guest.isAttending === null
                        ? ""
                        : guest.isAttending === 1
                        ? "true"
                        : "false"
                    }
                    onChange={(e) => {
                      const value = e.target.value;
                      updateGuest(
                        index,
                        "isAttending",
                        value === "" ? null : value === "true" ? 1 : 0
                      );
                    }}
                    sx={{
                      mt: 0.5,
                      flexDirection: { xs: "column", sm: "row" },
                      gap: { xs: 1, sm: 0 },
                    }}
                  >
                    <FormControlLabel
                      value="true"
                      control={<Radio />}
                      label={t("rsvpForm.yes")}
                      sx={{
                        fontFamily: "'Raleway', sans-serif",
                        mr: { xs: 0, sm: 3 },
                      }}
                    />
                    <FormControlLabel
                      value="false"
                      control={<Radio />}
                      label={t("rsvpForm.no")}
                      sx={{ fontFamily: "'Raleway', sans-serif" }}
                    />
                  </RadioGroup>
                  {formErrors[`attendance-${index}`] && (
                    <FormHelperText>
                      {t("rsvpForm.attendanceRequired")}
                    </FormHelperText>
                  )}
                </FormControl>
                <TextField
                  fullWidth
                  label={t("rsvpForm.foodPreferences")}
                  value={guest.foodPreferences}
                  onChange={(e) =>
                    updateGuest(index, "foodPreferences", e.target.value)
                  }
                  margin="dense"
                  multiline
                  rows={2}
                  sx={{
                    "& .MuiInputLabel-root": {
                      fontFamily: "'Raleway', sans-serif",
                    },
                    "& .MuiInputBase-root": {
                      fontFamily: "'Raleway', sans-serif",
                    },
                  }}
                />
              </Box>
            </React.Fragment>
          ))}
          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            sx={{
              mt: 2,
              fontFamily: "'Raleway', sans-serif",
              textTransform: "none",
              fontSize: { xs: "0.9rem", md: "1rem" },
              py: { xs: 1.5, md: 1.5 },
            }}
          >
            {t("rsvpForm.submit")}
          </Button>
          <Box sx={{ display: "flex", justifyContent: "center", pt: 1 }}>
            <img height={40} src="images/pikachu-pokemon.gif" alt="" />
          </Box>
        </form>
      </Paper>
    </>
  );
};
